import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getConsent, setConsent, loadAnalytics, clearAnalyticsCookies, COOKIE_SETTINGS_EVENT,
} from '../../utils/analytics';
import { color, gradient, radius, shadow } from '../../config/tokens';

// GDPR-style cookie consent. Analytics is opt-in: it loads only after the user
// clicks Accept, the choice is remembered, and it can be changed later via the
// footer "Cookie preferences" link (which dispatches COOKIE_SETTINGS_EVENT).
export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const choice = getConsent();
    if (choice === 'granted') {
      loadAnalytics();
    } else if (!choice) {
      setVisible(true);
    }
    const onOpen = () => setVisible(true);
    window.addEventListener(COOKIE_SETTINGS_EVENT, onOpen);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, onOpen);
  }, []);

  const accept = useCallback(() => {
    setConsent('granted');
    loadAnalytics();
    setVisible(false);
  }, []);

  const decline = useCallback(() => {
    setConsent('denied');
    clearAnalyticsCookies();
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1000,
        display: 'flex', justifyContent: 'center', padding: '1rem',
      }}
    >
      <div
        className="tc-fade-in"
        style={{
          maxWidth: '52rem', width: '100%',
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem',
          backgroundColor: color.bg.surface,
          border: `1px solid ${color.border.default}`,
          borderRadius: radius.lg,
          boxShadow: shadow.lg,
          padding: '1rem 1.25rem',
        }}
      >
        <p style={{ flex: '1 1 280px', margin: 0, color: color.text.secondary, fontSize: '0.875rem', lineHeight: 1.6 }}>
          We use cookies for analytics to understand how the Council is used. Analytics only runs if you accept.
          See our <Link to="/privacy" style={{ color: color.accent.light, textDecoration: 'underline' }}>Privacy Policy</Link>.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
          <button
            onClick={decline}
            className="tc-btn"
            style={{
              padding: '0.5rem 1rem', background: 'transparent', color: color.text.secondary,
              border: `1px solid ${color.border.default}`, borderRadius: radius.md,
              cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500,
            }}
          >
            Decline
          </button>
          <button
            onClick={accept}
            className="tc-btn"
            style={{
              padding: '0.5rem 1rem', background: gradient.primary, color: 'white',
              border: 'none', borderRadius: radius.md, cursor: 'pointer',
              fontSize: '0.875rem', fontWeight: 600,
            }}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
